import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'

import { ISFPExtractedDataSchema, type ISFPExtractedData } from '@/shared/types/isfp.types'
import {
  getBorderingTypeOptions,
  getDHWGeneratorTypeOptions,
  getEnergySourceOptions,
  getHeatingGeneratorTypeOptions,
  getHeatingTransferTypeOptions,
  getOrientationOptions,
  getRoofOrientationOptions,
  getVentilationTypeOptions,
} from '@/shared/utils/isfpEnumLabels'

export const useISFPFormController = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    trigger,
    setValue,
  } = useForm<ISFPExtractedData>({
    resolver: zodResolver(ISFPExtractedDataSchema),
    mode: 'onChange', // Validate on every change
    reValidateMode: 'onChange', // Re-validate on every change
    criteriaMode: 'all', // Show all validation errors
    defaultValues: {
      currentStateDescription: {
        lowerCompletion: undefined,
        basementEntrance: undefined,
        exteriorWalls: undefined,
        windows: undefined,
        upperCompletion: undefined,
        heating: undefined,
        heatingTransfer: undefined,
        drinkingHotWater: undefined,
        ventilation: undefined,
      },
      buildingInformation: {
        heatedVolumeInCbm: undefined,
        usableSpaceInSqm: undefined,
        constructionYear: undefined,
        numberOfResidentialUnits: undefined,
        numberOfFloors: undefined,
        meanFloorHeightInCm: undefined,
      },
      energeticValues: {
        finalEnergyDemandInKwh: undefined,
        auxEnergyDemandInKwh: undefined,
        energyConsumptionInKwh: undefined,
        primaryEnergyDemandInKwh: undefined,
        transmissionHeatLossInWPerK: undefined,
        equivalentCo2EmissionsInTons: undefined,
      },
      substance: {
        exteriorWallComponents: [],
        roofComponents: [],
        topFloorCeilingComponents: [],
        basementCeilingComponents: [],
        floorPlateComponents: [],
        basementExteriorWallComponents: [],
        windowComponents: [],
        doorComponents: [],
        roofWindowComponents: [],
      },
      systemTechnology: {
        heatingGeneration: [],
        drinkingHotWaterGeneration: [],
        heatingTransfer: [],
        ventilationSystem: [],
      },
    },
  })

  // Field arrays for substance components
  const {
    fields: exteriorWallFields,
    append: appendExteriorWall,
    remove: removeExteriorWall,
  } = useFieldArray({
    control,
    name: 'substance.exteriorWallComponents',
  })

  const {
    fields: roofFields,
    append: appendRoof,
    remove: removeRoof,
  } = useFieldArray({
    control,
    name: 'substance.roofComponents',
  })

  const {
    fields: topFloorCeilingFields,
    append: appendTopFloorCeiling,
    remove: removeTopFloorCeiling,
  } = useFieldArray({
    control,
    name: 'substance.topFloorCeilingComponents',
  })

  const {
    fields: basementCeilingFields,
    append: appendBasementCeiling,
    remove: removeBasementCeiling,
  } = useFieldArray({
    control,
    name: 'substance.basementCeilingComponents',
  })

  const {
    fields: floorPlateFields,
    append: appendFloorPlate,
    remove: removeFloorPlate,
  } = useFieldArray({
    control,
    name: 'substance.floorPlateComponents',
  })

  const {
    fields: basementExteriorWallFields,
    append: appendBasementExteriorWall,
    remove: removeBasementExteriorWall,
  } = useFieldArray({
    control,
    name: 'substance.basementExteriorWallComponents',
  })

  const {
    fields: windowFields,
    append: appendWindow,
    remove: removeWindow,
  } = useFieldArray({
    control,
    name: 'substance.windowComponents',
  })

  const {
    fields: doorFields,
    append: appendDoor,
    remove: removeDoor,
  } = useFieldArray({
    control,
    name: 'substance.doorComponents',
  })

  const {
    fields: roofWindowFields,
    append: appendRoofWindow,
    remove: removeRoofWindow,
  } = useFieldArray({
    control,
    name: 'substance.roofWindowComponents',
  })

  // Field arrays for system technology
  const {
    fields: heatingGenerationFields,
    append: appendHeatingGeneration,
    remove: removeHeatingGeneration,
  } = useFieldArray({
    control,
    name: 'systemTechnology.heatingGeneration',
  })

  const {
    fields: dhwGenerationFields,
    append: appendDHWGeneration,
    remove: removeDHWGeneration,
  } = useFieldArray({
    control,
    name: 'systemTechnology.drinkingHotWaterGeneration',
  })

  const {
    fields: heatingTransferFields,
    append: appendHeatingTransfer,
    remove: removeHeatingTransfer,
  } = useFieldArray({
    control,
    name: 'systemTechnology.heatingTransfer',
  })

  const {
    fields: ventilationSystemFields,
    append: appendVentilationSystem,
    remove: removeVentilationSystem,
  } = useFieldArray({
    control,
    name: 'systemTechnology.ventilationSystem',
  })

  // Get enum options with German labels
  const orientationOptions = getOrientationOptions()
  const roofOrientationOptions = getRoofOrientationOptions()
  const borderingTypeOptions = getBorderingTypeOptions()
  const heatingGeneratorTypeOptions = getHeatingGeneratorTypeOptions()
  const dhwGeneratorTypeOptions = getDHWGeneratorTypeOptions()
  const energySourceOptions = getEnergySourceOptions()
  const heatingTransferTypeOptions = getHeatingTransferTypeOptions()
  const ventilationTypeOptions = getVentilationTypeOptions()

  // Prefill function to populate form with data from API
  const prefillForm = (data: Partial<ISFPExtractedData>) => {
    reset(data)
  }

  // Scroll to first error field in the form
  const scrollToFirstError = useCallback(() => {
    // Get all field names with errors
    const firstErrorKey = Object.keys(errors)[0]

    if (!firstErrorKey) return

    // Attempt to find and focus the first invalid field
    // This handles nested errors (e.g., buildingInformation.heatedVolumeInCbm)
    const findAndScrollToField = (errorPath: string) => {
      // Build possible input names for nested fields
      const inputNames = [errorPath]

      // For nested errors, also try the full path
      if (errorPath.includes('.')) {
        const parts = errorPath.split('.')
        if (parts.length === 2) {
          // Handle simple nested fields like buildingInformation.heatedVolumeInCbm
          inputNames.push(errorPath)
        } else if (parts.length >= 3) {
          // Handle array fields like substance.exteriorWallComponents.0.areaInSqm
          inputNames.push(errorPath)
        }
      }

      // Try to find the element using various selectors
      for (const name of inputNames) {
        // Try by name attribute
        const elementByName = document.querySelector(`[name="${name}"]`) as HTMLElement
        if (elementByName) {
          elementByName.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => elementByName.focus(), 500)
          return true
        }

        // Try by id (MUI TextField often uses id)
        const elementById = document.getElementById(name) as HTMLElement
        if (elementById) {
          elementById.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => elementById.focus(), 500)
          return true
        }
      }

      // If it's a nested error, try to find the parent section
      if (errorPath.includes('.')) {
        const sectionName = errorPath.split('.')[0]
        // Find any input in that section
        const sectionInputs = document.querySelectorAll(`[name^="${sectionName}."]`)
        if (sectionInputs.length > 0) {
          const firstInput = sectionInputs[0] as HTMLElement
          firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => firstInput.focus(), 500)
          return true
        }
      }

      return false
    }

    // Handle nested errors by recursively searching for the first field with an error
    type ErrorObject = {
      message?: string
      [key: string]: unknown
    }

    const getFirstErrorPath = (
      obj: Record<string, ErrorObject | undefined>,
      parentPath = ''
    ): string | null => {
      for (const key in obj) {
        const currentPath = parentPath ? `${parentPath}.${key}` : key
        const currentError = obj[key]

        if (!currentError) continue

        // Check if this is a field error (has a message property)
        if ('message' in currentError && currentError.message) {
          return currentPath
        }

        // Check if it's an array error (numbered keys) or nested object
        if (typeof currentError === 'object') {
          // Recursively search nested objects
          const nestedPath = getFirstErrorPath(
            currentError as Record<string, ErrorObject | undefined>,
            currentPath
          )
          if (nestedPath) return nestedPath
        }
      }
      return null
    }

    const firstErrorPath = getFirstErrorPath(errors)
    if (firstErrorPath) {
      findAndScrollToField(firstErrorPath)
    }
  }, [errors])

  return {
    control,
    handleSubmit,
    errors,
    reset,
    watch,
    trigger,
    setValue,
    prefillForm,
    scrollToFirstError,
    // Substance field arrays
    exteriorWallFields,
    appendExteriorWall,
    removeExteriorWall,
    roofFields,
    appendRoof,
    removeRoof,
    topFloorCeilingFields,
    appendTopFloorCeiling,
    removeTopFloorCeiling,
    basementCeilingFields,
    appendBasementCeiling,
    removeBasementCeiling,
    floorPlateFields,
    appendFloorPlate,
    removeFloorPlate,
    basementExteriorWallFields,
    appendBasementExteriorWall,
    removeBasementExteriorWall,
    windowFields,
    appendWindow,
    removeWindow,
    doorFields,
    appendDoor,
    removeDoor,
    roofWindowFields,
    appendRoofWindow,
    removeRoofWindow,
    // System technology field arrays
    heatingGenerationFields,
    appendHeatingGeneration,
    removeHeatingGeneration,
    dhwGenerationFields,
    appendDHWGeneration,
    removeDHWGeneration,
    heatingTransferFields,
    appendHeatingTransfer,
    removeHeatingTransfer,
    ventilationSystemFields,
    appendVentilationSystem,
    removeVentilationSystem,
    // Enum options
    orientationOptions,
    roofOrientationOptions,
    borderingTypeOptions,
    heatingGeneratorTypeOptions,
    dhwGeneratorTypeOptions,
    energySourceOptions,
    heatingTransferTypeOptions,
    ventilationTypeOptions,
  }
}
